import Phaser from 'phaser';
import { TILE_SIZE, type FarmPlot } from '../data/world-config';
import { PLANTS, type PlantSpecies } from '../data/plants';
import type { GameScene } from '../scenes/GameScene';
import type { ActiveMonster } from './NightMonsterSystem';

// Sentinel behavior definitions per plant species
interface SentinelBehavior {
  type: 'ranged' | 'aoe' | 'heal' | 'snare' | 'multishot' | 'shield';
  range: number;         // detection/attack range in pixels
  interval: number;      // ms between attacks
  damage: number;        // base damage dealt
  description: string;   // shown in UI
  effectColor: number;   // projectile/effect color
  slowFactor?: number;   // multiplier on enemy speed (snare only)
  slowDuration?: number; // ms slow lasts (snare only)
  healAmount?: number;   // HP healed (heal only)
  damageReduction?: number; // fraction (shield only)
  projectileCount?: number; // multishot only
}

const SENTINEL_BEHAVIORS: Record<string, SentinelBehavior> = {
  sunflower: {
    type: 'ranged',
    range: 80,
    interval: 2000,
    damage: 9,
    description: 'Ranged shot',
    effectColor: 0xffdd44,
  },
  moonbloom: {
    type: 'heal',
    range: 60,
    interval: 5000,
    damage: 0,
    healAmount: 8,
    description: 'Heal aura',
    effectColor: 0x8b68c4,
  },
  fire_pepper: {
    type: 'aoe',
    range: 40,
    interval: 3000,
    damage: 15,
    description: 'Burst AOE',
    effectColor: 0xff4422,
  },
  shadow_vine: {
    type: 'snare',
    range: 50,
    interval: 4000,
    damage: 5,
    slowFactor: 0.5,
    slowDuration: 2000,
    description: 'Root snare',
    effectColor: 0x6633aa,
  },
  apple_tree: {
    type: 'multishot',
    range: 70,
    interval: 3500,
    damage: 5,
    projectileCount: 3,
    description: 'Multi-shot',
    effectColor: 0xcc4444,
  },
  pear_tree: {
    type: 'shield',
    range: 45,
    interval: 0, // passive
    damage: 0,
    damageReduction: 0.25,
    description: 'Shield aura',
    effectColor: 0x88cc44,
  },
  // Hybrids get stronger versions
  peapple: {
    type: 'multishot',
    range: 75,
    interval: 3000,
    damage: 7,
    projectileCount: 4,
    description: 'Mutant barrage',
    effectColor: 0xcc6644,
  },
  twilight_blossom: {
    type: 'aoe',
    range: 55,
    interval: 2500,
    damage: 12,
    description: 'Eclipse pulse',
    effectColor: 0xbb88ff,
  },
  infernal_creeper: {
    type: 'aoe',
    range: 50,
    interval: 2500,
    damage: 20,
    description: 'Hellfire burst',
    effectColor: 0xff6600,
  },
  solar_flare: {
    type: 'ranged',
    range: 90,
    interval: 1500,
    damage: 14,
    description: 'Corona beam',
    effectColor: 0xffaa00,
  },
  void_orchid: {
    type: 'shield',
    range: 55,
    interval: 0,
    damage: 0,
    damageReduction: 0.35,
    description: 'Void ward',
    effectColor: 0x4400aa,
  },
};

interface ActiveSentinel {
  plotKey: string;
  plot: FarmPlot;
  species: PlantSpecies;
  behavior: SentinelBehavior;
  worldX: number;
  worldY: number;
  lastAttackTime: number;
  rangeIndicator?: Phaser.GameObjects.Arc;
}

export class PlantSentinelSystem {
  private scene: GameScene;
  private sentinels: ActiveSentinel[] = [];
  private wasNight: boolean = false;
  sentinelsLost: number = 0;
  private sentinelProjectiles: { sprite: Phaser.GameObjects.Arc; vx: number; vy: number; damage: number; lifetime: number }[] = [];

  constructor(scene: GameScene) {
    this.scene = scene;
  }

  update(time: number, delta: number): void {
    const isNight = this.scene.dayNightSystem.isNight();

    if (isNight && !this.wasNight) {
      this.onNightStart();
    }
    if (!isNight && this.wasNight) {
      this.onNightEnd();
    }
    this.wasNight = isNight;

    if (isNight) {
      for (const sentinel of this.sentinels) {
        this.updateSentinel(sentinel, time);
      }
      this.updateProjectiles(delta);
    }
  }

  private onNightStart(): void {
    this.sentinelsLost = 0;
    this.rebuildSentinelList();

    // Show range indicators briefly
    for (const sentinel of this.sentinels) {
      const qualityMod = this.getQualityRangeMod(sentinel.plot.quality ?? 50);
      const range = sentinel.behavior.range * qualityMod;
      const indicator = this.scene.add.circle(
        sentinel.worldX, sentinel.worldY,
        range, sentinel.behavior.effectColor, 0.1
      );
      indicator.setStrokeStyle(1, sentinel.behavior.effectColor, 0.3);
      indicator.setDepth(3);
      sentinel.rangeIndicator = indicator;

      // Fade out after 3 seconds
      this.scene.tweens.add({
        targets: indicator,
        alpha: 0,
        duration: 1000,
        delay: 2000,
        onComplete: () => {
          indicator.destroy();
          sentinel.rangeIndicator = undefined;
        },
      });
    }

    if (this.sentinels.length > 0) {
      this.scene.events.emit('notification', `${this.sentinels.length} sentinel plant(s) defending your farm!`);
    }
  }

  private onNightEnd(): void {
    // Clean up range indicators
    for (const sentinel of this.sentinels) {
      sentinel.rangeIndicator?.destroy();
    }
    // Clean up projectiles
    for (const p of this.sentinelProjectiles) {
      p.sprite.destroy();
    }
    this.sentinelProjectiles = [];
  }

  private rebuildSentinelList(): void {
    this.sentinels = [];
    const plots = this.scene.farmSystem.getPlots();

    plots.forEach((plot, key) => {
      if (!plot.isDefender || !plot.plantId) return;
      if (plot.growthStage < 2) return; // must be mature or harvestable

      const species = PLANTS[plot.plantId];
      if (!species) return;

      const behavior = SENTINEL_BEHAVIORS[plot.plantId];
      if (!behavior) return;

      this.sentinels.push({
        plotKey: key,
        plot,
        species,
        behavior,
        worldX: plot.x * TILE_SIZE + TILE_SIZE / 2,
        worldY: plot.y * TILE_SIZE + TILE_SIZE / 2,
        lastAttackTime: 0,
      });
    });
  }

  private updateSentinel(sentinel: ActiveSentinel, time: number): void {
    // Skip passive abilities (shield)
    if (sentinel.behavior.type === 'shield') return;

    const interval = sentinel.behavior.interval;
    if (time - sentinel.lastAttackTime < interval) return;

    const qualityDmgMod = this.getQualityDamageMod(sentinel.plot.quality ?? 50);
    const qualityRangeMod = this.getQualityRangeMod(sentinel.plot.quality ?? 50);
    const range = sentinel.behavior.range * qualityRangeMod;

    if (sentinel.behavior.type === 'heal') {
      // Heal player if in range
      const player = this.scene.player.sprite;
      const dx = player.x - sentinel.worldX;
      const dy = player.y - sentinel.worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= range) {
        const healAmount = Math.floor((sentinel.behavior.healAmount ?? 8) * qualityDmgMod);
        this.scene.combatSystem.playerHp = Math.min(
          this.scene.combatSystem.playerMaxHp,
          this.scene.combatSystem.playerHp + healAmount
        );
        sentinel.lastAttackTime = time;
        this.scene.events.emit('combat-stats-update', this.scene.combatSystem.getStats());
        this.showHealEffect(sentinel.worldX, sentinel.worldY, sentinel.behavior.effectColor, healAmount);
      }
      return;
    }

    // Find closest monster in range
    const monsters = this.scene.nightMonsterSystem.getAliveMonsters();
    let closest: ActiveMonster | null = null;
    let closestDist = Infinity;

    for (const monster of monsters) {
      const dx = monster.sprite.x - sentinel.worldX;
      const dy = monster.sprite.y - sentinel.worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= range && dist < closestDist) {
        closest = monster;
        closestDist = dist;
      }
    }

    if (!closest) return;
    sentinel.lastAttackTime = time;

    const baseDamage = Math.floor(sentinel.behavior.damage * qualityDmgMod);

    switch (sentinel.behavior.type) {
      case 'ranged':
        this.fireProjectile(sentinel, closest, baseDamage);
        break;
      case 'aoe':
        this.fireAOE(sentinel, baseDamage, range, monsters);
        break;
      case 'snare':
        this.fireSnare(sentinel, closest, baseDamage);
        break;
      case 'multishot':
        this.fireMultishot(sentinel, monsters, baseDamage, range);
        break;
    }
  }

  private fireProjectile(sentinel: ActiveSentinel, target: ActiveMonster, damage: number): void {
    const dx = target.sprite.x - sentinel.worldX;
    const dy = target.sprite.y - sentinel.worldY;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const speed = 120;

    const projectile = this.scene.add.circle(sentinel.worldX, sentinel.worldY, 3, sentinel.behavior.effectColor, 0.9);
    projectile.setDepth(13);

    this.sentinelProjectiles.push({
      sprite: projectile,
      vx: (dx / dist) * speed,
      vy: (dy / dist) * speed,
      damage,
      lifetime: 2000,
    });

    // Muzzle flash at sentinel position
    this.showAttackFlash(sentinel.worldX, sentinel.worldY, sentinel.behavior.effectColor);
  }

  private fireAOE(sentinel: ActiveSentinel, damage: number, range: number, monsters: ActiveMonster[]): void {
    // Damage all monsters in range
    for (const monster of monsters) {
      const dx = monster.sprite.x - sentinel.worldX;
      const dy = monster.sprite.y - sentinel.worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= range) {
        this.scene.nightMonsterSystem.hitMonster(monster, damage, 60, 'special');
        this.showDamageNumber(monster.sprite.x, monster.sprite.y, damage, sentinel.behavior.effectColor);
      }
    }

    // AOE ring visual
    const ring = this.scene.add.circle(sentinel.worldX, sentinel.worldY, range, sentinel.behavior.effectColor, 0.15);
    ring.setStrokeStyle(2, sentinel.behavior.effectColor, 0.6);
    ring.setDepth(11);
    this.scene.tweens.add({
      targets: ring,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 400,
      onComplete: () => ring.destroy(),
    });
  }

  private fireSnare(sentinel: ActiveSentinel, target: ActiveMonster, damage: number): void {
    this.scene.nightMonsterSystem.hitMonster(target, damage, 30, 'light');
    this.showDamageNumber(target.sprite.x, target.sprite.y, damage, sentinel.behavior.effectColor);

    // Apply slow effect visually (tint purple, reduce speed temporarily)
    target.sprite.setTint(sentinel.behavior.effectColor);
    const originalSpeed = target.species.speed;
    const slowFactor = sentinel.behavior.slowFactor ?? 0.5;
    const slowDuration = sentinel.behavior.slowDuration ?? 2000;

    // Temporarily override the species speed
    (target as ActiveMonster & { _slowedSpeed?: number })._slowedSpeed = originalSpeed * slowFactor;

    this.scene.time.delayedCall(slowDuration, () => {
      if (!target.dead) {
        target.sprite.clearTint();
        delete (target as ActiveMonster & { _slowedSpeed?: number })._slowedSpeed;
      }
    });

    // Vine tendrils effect
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI * 2 * i) / 4;
      const vine = this.scene.add.circle(
        target.sprite.x + Math.cos(angle) * 8,
        target.sprite.y + Math.sin(angle) * 8,
        2, sentinel.behavior.effectColor, 0.7
      );
      vine.setDepth(12);
      this.scene.tweens.add({
        targets: vine,
        alpha: 0,
        duration: slowDuration,
        onComplete: () => vine.destroy(),
      });
    }
  }

  private fireMultishot(sentinel: ActiveSentinel, monsters: ActiveMonster[], damage: number, range: number): void {
    const count = sentinel.behavior.projectileCount ?? 3;
    const targetsInRange = monsters.filter(m => {
      const dx = m.sprite.x - sentinel.worldX;
      const dy = m.sprite.y - sentinel.worldY;
      return Math.sqrt(dx * dx + dy * dy) <= range;
    });

    if (targetsInRange.length === 0) return;

    for (let i = 0; i < count; i++) {
      const target = targetsInRange[i % targetsInRange.length];
      const dx = target.sprite.x - sentinel.worldX;
      const dy = target.sprite.y - sentinel.worldY;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const speed = 110;

      // Slight spread for visual interest
      const spread = (Math.random() - 0.5) * 0.3;

      const projectile = this.scene.add.circle(sentinel.worldX, sentinel.worldY, 2, sentinel.behavior.effectColor, 0.8);
      projectile.setDepth(13);

      this.scene.time.delayedCall(i * 100, () => {
        this.sentinelProjectiles.push({
          sprite: projectile,
          vx: (dx / dist) * speed + spread * speed,
          vy: (dy / dist) * speed + spread * speed,
          damage,
          lifetime: 2000,
        });
      });
    }

    this.showAttackFlash(sentinel.worldX, sentinel.worldY, sentinel.behavior.effectColor);
  }

  private updateProjectiles(delta: number): void {
    const dt = delta / 1000;

    for (let i = this.sentinelProjectiles.length - 1; i >= 0; i--) {
      const p = this.sentinelProjectiles[i];
      p.lifetime -= delta;

      if (p.lifetime <= 0) {
        p.sprite.destroy();
        this.sentinelProjectiles.splice(i, 1);
        continue;
      }

      p.sprite.x += p.vx * dt;
      p.sprite.y += p.vy * dt;

      // Check hits against monsters
      const monsters = this.scene.nightMonsterSystem.getAliveMonsters();
      for (const monster of monsters) {
        const dx = monster.sprite.x - p.sprite.x;
        const dy = monster.sprite.y - p.sprite.y;
        if (Math.sqrt(dx * dx + dy * dy) < 12) {
          this.scene.nightMonsterSystem.hitMonster(monster, p.damage, 50, 'light');
          this.showDamageNumber(monster.sprite.x, monster.sprite.y, p.damage, 0x6aaa3a);
          p.sprite.destroy();
          this.sentinelProjectiles.splice(i, 1);
          break;
        }
      }
    }
  }

  // Quality modifiers
  private getQualityDamageMod(quality: number): number {
    if (quality >= 80) return 1.3;
    if (quality >= 50) return 1.0;
    return 0.7;
  }

  private getQualityRangeMod(quality: number): number {
    if (quality >= 80) return 1.2;
    if (quality >= 50) return 1.0;
    return 0.8;
  }

  /** Check if player is within any shield sentinel's range. Returns damage reduction factor (0 to 1). */
  getShieldReduction(): number {
    if (!this.scene.dayNightSystem.isNight()) return 0;

    const playerX = this.scene.player.sprite.x;
    const playerY = this.scene.player.sprite.y;
    let maxReduction = 0;

    for (const sentinel of this.sentinels) {
      if (sentinel.behavior.type !== 'shield') continue;
      const dx = playerX - sentinel.worldX;
      const dy = playerY - sentinel.worldY;
      const qualityRangeMod = this.getQualityRangeMod(sentinel.plot.quality ?? 50);
      const range = sentinel.behavior.range * qualityRangeMod;
      if (Math.sqrt(dx * dx + dy * dy) <= range) {
        const reduction = sentinel.behavior.damageReduction ?? 0.25;
        const qualityDmgMod = this.getQualityDamageMod(sentinel.plot.quality ?? 50);
        maxReduction = Math.max(maxReduction, reduction * qualityDmgMod);
      }
    }

    return Math.min(maxReduction, 0.5); // cap at 50% reduction
  }

  /** Get sentinel positions for monster targeting. */
  getSentinelPositions(): { x: number; y: number; plotKey: string }[] {
    return this.sentinels.map(s => ({
      x: s.worldX,
      y: s.worldY,
      plotKey: s.plotKey,
    }));
  }

  /** Called when a monster destroys a sentinel plant. */
  destroySentinel(plotKey: string): void {
    const idx = this.sentinels.findIndex(s => s.plotKey === plotKey);
    if (idx === -1) return;

    const sentinel = this.sentinels[idx];
    sentinel.rangeIndicator?.destroy();

    // Clear the plot's plant and defender status
    sentinel.plot.plantId = undefined;
    sentinel.plot.growthStage = 0;
    sentinel.plot.isDefender = false;

    // Destroy the plant sprite
    const plantSprites = this.scene.farmSystem.getPlantSprites();
    const sprite = plantSprites.get(plotKey);
    if (sprite) {
      // Death animation
      this.scene.tweens.add({
        targets: sprite,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        angle: 45,
        duration: 400,
        onComplete: () => sprite.destroy(),
      });
      plantSprites.delete(plotKey);
    }

    this.sentinels.splice(idx, 1);
    this.sentinelsLost++;
    this.scene.events.emit('notification', `Sentinel ${sentinel.species.name} was destroyed!`);
    this.scene.events.emit('sentinel-destroyed', { plantId: sentinel.species.id, plotKey });
  }

  getActiveSentinelCount(): number {
    return this.sentinels.length;
  }

  // Visual effects
  private showAttackFlash(x: number, y: number, color: number): void {
    const flash = this.scene.add.circle(x, y, 6, color, 0.5);
    flash.setDepth(12);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 200,
      onComplete: () => flash.destroy(),
    });
  }

  private showHealEffect(x: number, y: number, color: number, amount: number): void {
    // Heal ring
    const ring = this.scene.add.circle(x, y, 20, color, 0.2);
    ring.setStrokeStyle(1, color, 0.5);
    ring.setDepth(11);
    this.scene.tweens.add({
      targets: ring,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 600,
      onComplete: () => ring.destroy(),
    });

    // Heal number on player
    const player = this.scene.player.sprite;
    const text = this.scene.add.text(player.x, player.y - 10, `+${amount}`, {
      fontFamily: 'monospace',
      fontSize: '9px',
      color: '#88ff88',
      stroke: '#000000',
      strokeThickness: 3,
      fontStyle: 'bold',
    });
    text.setOrigin(0.5, 1);
    text.setDepth(20);
    this.scene.tweens.add({
      targets: text,
      y: text.y - 20,
      alpha: 0,
      duration: 700,
      onComplete: () => text.destroy(),
    });
  }

  private showDamageNumber(x: number, y: number, damage: number, color: number): void {
    const colorStr = `#${color.toString(16).padStart(6, '0')}`;
    const text = this.scene.add.text(x, y - 8, `${damage}`, {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: colorStr,
      stroke: '#000000',
      strokeThickness: 2,
    });
    text.setOrigin(0.5, 1);
    text.setDepth(20);
    this.scene.tweens.add({
      targets: text,
      y: y - 25,
      alpha: 0,
      duration: 600,
      onComplete: () => text.destroy(),
    });
  }

  shutdown(): void {
    for (const sentinel of this.sentinels) {
      sentinel.rangeIndicator?.destroy();
    }
    for (const p of this.sentinelProjectiles) {
      p.sprite.destroy();
    }
    this.sentinelProjectiles = [];
    this.sentinels = [];
  }
}
